// Band-limited fractional resampler: Kaiser-windowed sinc evaluated from a lookup table.
//
// The original sketches pick every Nth sample (`songData[index]`, index += 10) with no filter first.
// Everything above the new Nyquist frequency then folds back into the audible band (aliasing).
// Here each output sample is a weighted sum of the neighbouring input samples, with the low-pass cutoff
// chosen per call so it can follow the local point density of the groove.

const ZC = 8; // zero crossings each side of the kernel
const OVERSAMPLE = 512; // table entries per unit of kernel argument
const BETA = 7.5; // Kaiser beta (~ -70 dB sidelobes)

function besselI0(x: number): number {
  let sum = 1, term = 1;
  const q = (x * x) / 4;
  for (let k = 1; k < 50; k++) {
    term *= q / (k * k);
    sum += term;
    if (term < sum * 1e-12) break;
  }
  return sum;
}

const TABLE = (() => {
  const n = ZC * OVERSAMPLE + 2;
  const t = new Float64Array(n);
  const i0b = besselI0(BETA);
  for (let i = 0; i < n; i++) {
    const u = i / OVERSAMPLE;
    if (u >= ZC) { t[i] = 0; continue; }
    const sinc = u === 0 ? 1 : Math.sin(Math.PI * u) / (Math.PI * u);
    const r = u / ZC;
    t[i] = sinc * (besselI0(BETA * Math.sqrt(1 - r * r)) / i0b);
  }
  return t;
})();

function kernel(u: number): number {
  const a = Math.abs(u) * OVERSAMPLE;
  const i = a | 0;
  const fr = a - i;
  return TABLE[i] + (TABLE[i + 1] - TABLE[i]) * fr;
}

export class Resampler {
  constructor(private readonly x: ArrayLike<number>) {}

  get length(): number {
    return this.x.length;
  }

  /**
   * Value at fractional input index `pos`, band-limited to `cutoff` (cycles per input sample, ≤ 0.5).
   * Out-of-range samples count as silence.
   */
  sample(pos: number, cutoff = 0.5): number {
    const x = this.x;
    const fc = Math.min(0.5, Math.max(1e-4, cutoff));
    const scale = 2 * fc; // kernel stretch: lower cutoff -> wider kernel
    const half = ZC / scale;
    const lo0 = Math.ceil(pos - half), hi0 = Math.floor(pos + half);
    const lo = Math.max(0, lo0), hi = Math.min(x.length - 1, hi0);
    let acc = 0, wsum = 0;
    for (let n = lo; n <= hi; n++) {
      const w = kernel((n - pos) * scale);
      acc += w * x[n];
      wsum += w;
    }
    // Inside the signal: divide by the actual weight sum (exact unity DC gain).
    // At the edges the missing taps are silence, so use the ideal weight sum (≈ 1/scale) instead.
    if (lo === lo0 && hi === hi0) return wsum === 0 ? 0 : acc / wsum;
    return acc * scale;
  }
}

/** Resample a whole signal from rate `fsIn` to `fsOut`, with anti-alias filtering when downsampling. */
export function resampleSignal(x: Float32Array, fsIn: number, fsOut: number): Float32Array {
  const ratio = fsIn / fsOut;
  const n = Math.floor(x.length / ratio);
  const out = new Float32Array(n);
  const r = new Resampler(x);
  const cutoff = Math.min(0.5, 0.45 / ratio);
  for (let i = 0; i < n; i++) out[i] = r.sample(i * ratio, cutoff);
  return out;
}
